/**
 * Progress-line motion model: the painted value has its own clock and creeps toward a CEILING
 * derived from the ready-photo share — the same truth the «N из M» counter shows. Real signals
 * only move the ceiling; the genuine "all done" is the only thing that lifts it to a true 100.
 *
 * Two phases share the ceiling: bytes carry 0…BYTES_CEILING, confirmed previews carry the rest
 * up to the cap. Timing (drift, done-grace, hold, latch) lives in `nextUploadProgress`, so the
 * bar component only paints — it owns no rules.
 */

/** The signal-driven ceiling tops out at 96 — 100 is earned only by real completion. */
export const CEILING_HEADROOM = 4;
/** Per-tick share of the remaining gap — exponential approach at a ~16ms tick. */
export const CREEP_FRACTION = 0.045;
/** Hard floor on per-tick advance so the bar mathematically never freezes below the ceiling. */
export const CREEP_MIN_STEP = 0.04;
export const SNAP_EPSILON = 0.05;

/** Top of the signal-driven ceiling. */
export const PROGRESS_CAP = 100 - CEILING_HEADROOM;
/** Byte transfer owns 0…60; confirmed previews own 60…96. */
export const BYTES_CEILING = 60;
/** τ of the processing-phase drift: silence creeps the ceiling toward «real + 4» on this exponential. */
export const PROGRESS_DRIFT_MS = 12_000;
/** «Finished» at the latest this long after the server said done, even if previews still trickle. */
export const PROGRESS_DONE_GRACE_MS = 8_000;
/** «Готово» stays this long; then the run latches and later counter jitter cannot reopen it. */
export const PROGRESS_DONE_HOLD_MS = 5_000;

export function clampPercent(percent: number): number {
  if (!Number.isFinite(percent)) return 0;
  return Math.min(100, Math.max(0, percent));
}

/** The bar mirrors the counter: ceiling = share of photos actually ready, capped below 100. */
export function uploadCeiling(loaded: number, total: number): number {
  if (!(total > 0)) return 0;
  const readyFrac = clampPercent((loaded / total) * 100) / 100;
  return readyFrac * PROGRESS_CAP;
}

/** One tick toward the target; never moves backward unless the caller allows a retreat. */
export function creepToward(current: number, target: number, options?: { allowRetreat?: boolean }): number {
  if (target <= current) {
    if (!options?.allowRetreat) return current;
    const eased = current + (target - current) * CREEP_FRACTION;
    return Math.abs(target - eased) < SNAP_EPSILON ? target : eased;
  }

  const gap = target - current;
  if (gap < SNAP_EPSILON) return target;
  const step = Math.max(gap * CREEP_FRACTION, Math.min(CREEP_MIN_STEP, gap));
  const next = current + step;
  return Math.abs(target - next) < SNAP_EPSILON ? target : next;
}

const MB = 1024 * 1024;
/** Above this the counter switches to gigabytes — BOTH numbers together, never a mixed pair. */
const GB_SWITCH_MB = 1000;

/**
 * «155 из 220 МБ» · «0,9 из 2,3 ГБ». The TOTAL picks the unit, so the left number never changes
 * unit mid-upload; 1024-based, Russian decimal comma, no latin, no slash.
 */
export function formatUploadSize(bytesSent: number, bytesTotal: number): string {
  const totalMb = Math.max(0, bytesTotal) / MB;
  const sentMb = Math.min(Math.max(0, bytesSent) / MB, totalMb);
  if (totalMb < GB_SWITCH_MB) return `${Math.round(sentMb)} из ${Math.round(totalMb)} МБ`;
  const gb = (mb: number): string => (mb / 1024).toFixed(1).replace('.', ',');
  return `${gb(sentMb)} из ${gb(totalMb)} ГБ`;
}

/** One polled snapshot of the whole batch, whatever product produced it. */
export interface UploadProgressSignal {
  readonly bytesSent: number;
  readonly bytesTotal: number;
  /** Images whose preview the server confirmed as painted. */
  readonly previewsReady: number;
  readonly imagesTotal: number;
  /**
   * Images actually painted in the grid (loaded and decoded) — the finish gate. Absent ⇒ the
   * product tracks no paint and the server count stands in.
   */
  readonly painted?: number;
  /** The server says the batch is processed. */
  readonly serverDone: boolean;
  /** Terminal failure — nothing more will arrive for this batch. */
  readonly failed: boolean;
}

export type UploadProgressPhase = 'idle' | 'bytes' | 'processing' | 'done' | 'failed';

export interface UploadProgressState {
  /** Ceiling the bar creeps toward, 0…96 while running, 100 when done. */
  readonly target: number;
  readonly phase: UploadProgressPhase;
  /** The bar has something to show (false = folded away). */
  readonly visible: boolean;
  /** Run closed for good — counter jitter can no longer reopen it. */
  readonly latched: boolean;
  readonly movedAt: number;
  readonly doneAt: number | null;
  readonly finishedAt: number | null;
  readonly signal: UploadProgressSignal;
}

const EMPTY_SIGNAL: UploadProgressSignal = {
  bytesSent: 0,
  bytesTotal: 0,
  previewsReady: 0,
  imagesTotal: 0,
  serverDone: false,
  failed: false,
};

export function initialUploadProgress(): UploadProgressState {
  return {
    target: 0,
    phase: 'idle',
    visible: false,
    latched: false,
    movedAt: 0,
    doneAt: null,
    finishedAt: null,
    signal: EMPTY_SIGNAL,
  };
}

function share(part: number, whole: number): number {
  if (!(whole > 0) || !Number.isFinite(part)) return 0;
  return Math.min(1, Math.max(0, part / whole));
}

/** Two-phase ceiling: bytes buy 0…60, confirmed previews buy 60…96. */
export function uploadProgressTarget(signal: UploadProgressSignal): number {
  const bytes = share(signal.bytesSent, signal.bytesTotal) * BYTES_CEILING;
  const processing = share(signal.previewsReady, signal.imagesTotal) * (PROGRESS_CAP - BYTES_CEILING);
  return bytes + processing;
}

/** Silence in the processing phase creeps the ceiling toward «real + 4» with τ = PROGRESS_DRIFT_MS. */
export function driftedTarget(real: number, silentMs: number): number {
  return real + CEILING_HEADROOM * (1 - Math.exp(-Math.max(0, silentMs) / PROGRESS_DRIFT_MS));
}

function moved(a: UploadProgressSignal, b: UploadProgressSignal): boolean {
  return (
    a.bytesSent !== b.bytesSent ||
    a.bytesTotal !== b.bytesTotal ||
    a.previewsReady !== b.previewsReady ||
    a.imagesTotal !== b.imagesTotal ||
    a.serverDone !== b.serverDone
  );
}

/**
 * Fold one polled snapshot into the run. `now` is monotonic ms — the caller owns the clock, so
 * every boundary below is unit-testable without timers.
 */
export function nextUploadProgress(prev: UploadProgressState, signal: UploadProgressSignal, now: number): UploadProgressState {
  // Terminal failure wins over every other rule — the bar stops at the real success share.
  if (signal.failed) {
    return {
      ...prev,
      signal,
      target: share(signal.previewsReady, signal.imagesTotal) * 100,
      phase: 'failed',
      visible: true,
      latched: true,
      finishedAt: prev.finishedAt ?? now,
    };
  }

  const working = signal.imagesTotal > 0;
  // A latched run only reopens for a genuinely NEW batch. The tail of the old one still carries
  // `serverDone`, so trailing preview confirmations can never restart the bar.
  if (prev.latched) {
    if (!working || signal.serverDone) return { ...prev, signal };
    return nextUploadProgress({ ...initialUploadProgress(), movedAt: now }, signal, now);
  }

  if (!working) return { ...prev, signal, phase: 'idle', visible: false, target: 0 };

  const movedAt = moved(prev.signal, signal) ? now : prev.movedAt;
  const doneAt = signal.serverDone ? (prev.doneAt ?? now) : null;
  // Finish = every image painted in the grid; a product without paint tracking finishes on the server count.
  const allPainted = (signal.painted ?? signal.previewsReady) >= signal.imagesTotal;
  const graceOver = doneAt !== null && now - doneAt >= PROGRESS_DONE_GRACE_MS;
  const finished = allPainted || graceOver;
  const finishedAt = finished ? (prev.finishedAt ?? now) : null;
  const latched = finishedAt !== null && now - finishedAt >= PROGRESS_DONE_HOLD_MS;

  const phase = finished ? 'done' : signal.bytesSent < signal.bytesTotal ? 'bytes' : 'processing';
  const real = uploadProgressTarget(signal);
  // Each ready preview lifts the anchor; the drift never gives back what it already climbed.
  const drifted = Math.min(real + CEILING_HEADROOM, Math.max(prev.target, driftedTarget(real, now - movedAt)));

  return {
    signal,
    target: finished ? 100 : phase === 'processing' ? drifted : real,
    phase,
    visible: !latched,
    latched,
    movedAt,
    doneAt,
    finishedAt,
  };
}
